import { action } from '@reatom/framework'

import { pipe, Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { pairRegistry } from '@root/entities/pair/atoms/pair-registry'
import { TrackingWallet } from '@root/entities/wallet-tracking'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'

import { TrackedWalletSwapAlert } from '../model'

import type {
  WalletsTrackerEditTrackingWalletCreatePayload as WalletsTrackerEditTrackingWalletCreatePayloadDto,
  WalletsTrackerImportTrackingWalletsCreatePayload,
} from '@blade/trade-api-client'
import type { DelugeId } from '@root/entities/user-short'
import type { WalletAddress } from '@root/entities/wallet'

export const getUserTrackingWalletsRequestAction = action(
  async (
    ctx,
    { network, delugeId }: { network: Network.Network; delugeId: DelugeId },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.walletsTracker.walletsTrackerGetTrackingWalletsList,
        [
          {
            network: Network.toDto(network),
            delugeId,
          },
        ],
      ),
    )

    return pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
      Result.map((it) => it.map((it) => TrackingWallet.fromDto(it))),
    )
  },
  'getUserTrackingWalletsRequestAction',
)

export interface EditTrackingWalletPayload
  extends WalletsTrackerEditTrackingWalletCreatePayloadDto {
  walletAddress: WalletAddress
}

export const editUserTrackingWalletRequestAction = action(
  async (ctx, req: EditTrackingWalletPayload) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.walletsTracker.walletsTrackerEditTrackingWalletCreate,
        [
          {
            ...req,
          },
        ],
      ),
    )

    return pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
      Result.map((it) => TrackingWallet.fromDto(it)),
    )
  },
  'editUserTrackingWalletRequestAction',
)

export type ImportWalletTrackerReq =
  WalletsTrackerImportTrackingWalletsCreatePayload[number]
export const importWalletTrackerRequestAction = action(
  async (ctx, req: Array<ImportWalletTrackerReq>) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.walletsTracker.walletsTrackerImportTrackingWalletsCreate,
        [req],
      ),
    )

    return pipe(result, Result.mapError(ApiError.fallbackToUnknown))
  },
  'importWalletTrackerRequestAction',
)

export const deleteUserTrackingWalletRequestAction = action(
  async (
    ctx,
    {
      network,
      walletAddress,
    }: { network: Network.Network; walletAddress: WalletAddress },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.walletsTracker.walletsTrackerDeleteTrackingWalletCreate,
        [
          {
            network: Network.toDto(network),
            walletAddress,
          },
        ],
      ),
    )

    return pipe(result, Result.mapError(ApiError.fallbackToUnknown))
  },
  'deleteUserTrackingWalletRequestAction',
)

export const fetchWalletTrackerAlertsRequestAction = action(
  async (ctx, { network }: { network: Network.Network }) => {
    const {
      tradeApiClient,
      environment: {
        assets: { tokenLogosHost, avatarsHost },
      },
    } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.walletsTracker.walletsTrackerGetAlertsList,
        [
          {
            network: Network.toDto(network),
            limit: 50,
            sort: 'desc',
          },
        ],
      ),
    )

    return pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
      Result.map((it) => {
        const data = it.data.map((it) => {
          const pair = it.pairInfo
            ? pairRegistry.resolveFromDto(ctx, it.pairInfo)
            : undefined
          return TrackedWalletSwapAlert.fromDto(
            it,
            tokenLogosHost,
            avatarsHost,
            pair,
          )
        })

        return {
          ...it,
          data: data,
        }
      }),
    )
  },
  'fetchWalletTrackerAlertsRequestAction',
)
